import { pathOr, over, lensPath, append, assoc, always, compose, defaultTo, adjust, remove, prop, cond, insert, nth, set, lensIndex, T, equals, length } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import { BASE_PADDING_DOCUMENT } from '../_CONST';
import { ACTIONS, TARGET_TYPES, buildEntry } from '../lib/sessionHistory';

export const defaultPage = {
  header: [],
  widgets: [],
  footer: [],
  breakPage: true,
  padding: BASE_PADDING_DOCUMENT,
  rules: [],
  backgroundImage: {
    url: '',
    size: 'contain',
    position: 'center center',
    pendingFile: null
  }
};

export const useSections = (state, setState, pushEvent) => {
  const pathPages = ['values', 'pages'];
  const pages = pathOr([], pathPages, state);
  const actionToSection = (actionToDo) => over(lensPath(pathPages), actionToDo);
  const emit = (entry) => { if (pushEvent) pushEvent(entry); };

  const compositionEntry = (sectionIndex) => buildEntry({
    action: ACTIONS.COMPOSITION_UPDATE,
    targetType: TARGET_TYPES.SECTION,
    sectionIndex
  });
  const conditionsEntry = (sectionIndex) => buildEntry({
    action: ACTIONS.CONDITIONS_UPDATE,
    targetType: TARGET_TYPES.SECTION,
    sectionIndex
  });

  const addSection = () => setState(actionToSection(append(defaultPage)));

  const updatePaddingSection = (index) => (key, value) => {
    emit(compositionEntry(index));
    setState(
      over(lensPath([...pathPages, index, 'padding']), assoc(key, value))
    );
  };

  const setPendingBackgroundImageSection = (index) => (file) =>
    setState(over(lensPath([...pathPages, index, 'backgroundImage']), always({
      url: '',
      size: 'contain',
      position: 'center center',
      pendingFile: file
    })));

  const clearBackgroundImageSection = (index) =>
    setState(over(lensPath([...pathPages, index, 'backgroundImage']), always({
      url: '',
      size: 'contain',
      position: 'center center',
      pendingFile: null
    })));

  const updateBackgroundSizeSection = (index) => (value) =>
    setState(over(lensPath([...pathPages, index, 'backgroundImage', 'size']), always(value)));

  const updateBackgroundPositionSection = (index) => (value) =>
    setState(over(lensPath([...pathPages, index, 'backgroundImage', 'position']), always(value)));

  const addRuleSection = (index) => {
    emit(conditionsEntry(index));
    setState(over(
      lensPath([...pathPages, index, 'rules']),
      compose(
        append({}),
        defaultTo([])
      )
    ));
  };

  const updateRuleSection = (index) => (ruleIndex) => (fn) => {
    emit(conditionsEntry(index));
    setState(over(
      lensPath([...pathPages, index, 'rules']),
      adjust(ruleIndex, fn)
    ));
  };

  const removeRuleSection = (index) => (ruleIndex) => () => {
    emit(conditionsEntry(index));
    setState(over(
      lensPath([...pathPages, index, 'rules']),
      remove(ruleIndex, 1)
    ));
  };

  const removeSection = (index) => {
    emit(buildEntry({
      action: ACTIONS.DELETION,
      targetType: TARGET_TYPES.SECTION,
      sectionIndex: index
    }));
    setState(actionToSection(remove(index, 1)));
    if (isNotNilOrEmpty(prop('previewHtml', state))) {
      setState(assoc('previewHtml', remove(index, 1, prop('previewHtml', state))));
    }
  };

  const editSections = (index, action, oldIndex) => {
    const creation = (sectionIndex) => emit(buildEntry({
      action: ACTIONS.CREATION,
      targetType: TARGET_TYPES.SECTION,
      sectionIndex
    }));
    const composition = (sectionIndex) => emit(buildEntry({
      action: ACTIONS.COMPOSITION_UPDATE,
      targetType: TARGET_TYPES.SECTION,
      sectionIndex
    }));
    cond([
      [equals('add'), () => { addSection(); creation(length(pages)); }],
      [equals('remove'), () => removeSection(index)],
      [equals('addSectionAfter'), () => { setState(actionToSection(insert(index + 1, defaultPage))); creation(index + 1); }],
      [equals('addSectionBefore'), () => { setState(actionToSection(insert(index, defaultPage))); creation(index); }],
      [equals('duplicateSection'), () => { setState(actionToSection(append(nth(index, pages)))); creation(length(pages)); }],
      [equals('swapSections'), () => {
        setState(actionToSection(compose(
          set(lensIndex(index), nth(oldIndex, pages)),
          set(lensIndex(oldIndex), nth(index, pages))
        )));
        composition(index);
      }],
      [equals('moveSectionBefore'), () => {
        setState(actionToSection(compose(
          insert(oldIndex, nth(index, pages)),
          remove(index, 1)
        )));
        composition(oldIndex);
      }],
      [T, () => state]
    ])(action);
  };

  return {
    editSections,
    updatePaddingSection,
    setPendingBackgroundImageSection,
    clearBackgroundImageSection,
    updateBackgroundSizeSection,
    updateBackgroundPositionSection,
    addRuleSection,
    updateRuleSection,
    removeRuleSection
  };
};
